import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import KPICard from "../components/KPICard";
import { useTheme } from "../DataContext";
import { DEMO_PROJECTS } from "../demoData";

// Simulated dependency graph
const DEPENDENCIES = [
  { from: "P001", to: "P005", type: "FS", risk: "high",   desc: "ERP data model must be finalized before Analytics Platform builds connectors" },
  { from: "P001", to: "P004", type: "SS", risk: "medium", desc: "ERP security requirements drive Cybersecurity Framework scope" },
  { from: "P002", to: "P010", type: "FS", risk: "high",   desc: "Cloud infrastructure required for AI/ML platform deployment" },
  { from: "P002", to: "P001", type: "FS", risk: "medium", desc: "ERP modernization migration depends on cloud readiness" },
  { from: "P003", to: "P008", type: "FF", risk: "critical",desc: "Security framework completion is a prerequisite for regulatory compliance sign-off" },
  { from: "P004", to: "P003", type: "SS", risk: "medium", desc: "Cybersecurity framework defines risk controls used in security project" },
  { from: "P005", to: "P011", type: "FS", risk: "low",    desc: "ESG reporting consumes data from analytics platform" },
  { from: "P006", to: "P001", type: "SF", risk: "medium", desc: "Supply chain optimization requires ERP integration point" },
  { from: "P009", to: "P004", type: "FS", risk: "medium", desc: "Mobile app security review depends on cybersecurity framework" },
  { from: "P012", to: "P002", type: "FS", risk: "high",   desc: "Network upgrade must complete before cloud migration begins" },
  { from: "P007", to: "P009", type: "FS", risk: "low",    desc: "HR platform provides user data for mobile app employee features" },
];

const TYPE_LABELS = { FS: "Finish-to-Start", SS: "Start-to-Start", FF: "Finish-to-Finish", SF: "Start-to-Finish" };

export default function InterdependencyRisk() {
  const { theme } = useTheme();
  const [selected, setSelected] = useState(null);

  const riskColor = (r) => r === "critical" ? theme.danger : r === "high" ? "#FF6B35" : r === "medium" ? theme.warning : theme.success;
  const criticalLinks = DEPENDENCIES.filter(d => d.risk === "critical" || d.risk === "high");

  // Build adjacency — projects that block others
  const blockingCount = {};
  const blockedCount  = {};
  DEPENDENCIES.forEach(d => {
    blockingCount[d.from] = (blockingCount[d.from] || 0) + 1;
    blockedCount[d.to]    = (blockedCount[d.to]    || 0) + 1;
  });

  // Node positions for SVG dependency map
  const nodePositions = {
    P001: { x: 300, y: 100 }, P002: { x: 550, y: 60  }, P003: { x: 150, y: 200 },
    P004: { x: 420, y: 200 }, P005: { x: 300, y: 320 }, P006: { x: 150, y: 350 },
    P007: { x: 550, y: 280 }, P008: { x: 50,  y: 300 }, P009: { x: 450, y: 360 },
    P010: { x: 680, y: 160 }, P011: { x: 170, y: 430 }, P012: { x: 680, y: 380 },
  };

  const getProject = (id) => DEMO_PROJECTS.find(p => p.id === id);

  return (
    <PageLayout
      title="Interdependency & Dependency Risk"
      subtitle="Cross-project dependency mapping, blocking relationships, cascade failure scoring — P-08"
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "14px", marginBottom: "20px" }}>
        <KPICard label="Total Dependencies"   value={DEPENDENCIES.length} icon="⬟" color={theme.accent}  sub="Mapped links" />
        <KPICard label="Critical/High Risk"   value={criticalLinks.length} icon="⚠" color={theme.danger}  sub="Blocking relationships" />
        <KPICard label="Most Blocking"        value="P002" icon="◈" color={theme.warning} sub={`Blocks ${blockingCount["P002"] || 0} projects`} />
        <KPICard label="Cascade Risk Score"   value="HIGH" icon="◉" color={theme.danger}  sub="If P003 fails → P008 fails" />
      </div>

      {/* Dependency Map SVG */}
      <div className="pmo-card" style={{ marginBottom: "20px" }}>
        <div className="section-title">Dependency Network Map</div>
        <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "10px" }}>Click a project node to see its dependencies</div>
        <div style={{ overflow: "auto" }}>
          <svg width="750" height="520" style={{ display: "block", margin: "0 auto" }}>
            {/* Dependency arrows */}
            <defs>
              {["critical","high","medium","low"].map(risk => (
                <marker key={risk} id={`arrow-${risk}`} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                  <path d="M0,0 L0,6 L8,3 z" fill={riskColor(risk)} />
                </marker>
              ))}
            </defs>
            {DEPENDENCIES.map((dep, i) => {
              const from = nodePositions[dep.from];
              const to   = nodePositions[dep.to];
              if (!from || !to) return null;
              const highlighted = selected === dep.from || selected === dep.to;
              return (
                <line key={i}
                  x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                  stroke={highlighted ? riskColor(dep.risk) : `${riskColor(dep.risk)}55`}
                  strokeWidth={highlighted ? 2.5 : 1.5}
                  strokeDasharray={dep.type === "SS" ? "6,3" : dep.type === "FF" ? "2,3" : "none"}
                  markerEnd={`url(#arrow-${dep.risk})`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelected(null)}
                />
              );
            })}
            {/* Project nodes */}
            {DEMO_PROJECTS.map(p => {
              const pos = nodePositions[p.id];
              if (!pos) return null;
              const color = p.rag === "red" ? theme.danger : p.rag === "amber" ? theme.warning : theme.success;
              const isSelected = selected === p.id;
              const blocking = blockingCount[p.id] || 0;
              return (
                <g key={p.id} style={{ cursor: "pointer" }} onClick={() => setSelected(isSelected ? null : p.id)}>
                  <circle cx={pos.x} cy={pos.y} r={22} fill={`${color}${isSelected ? "44" : "22"}`} stroke={color} strokeWidth={isSelected ? 2.5 : 1.5} />
                  {blocking > 0 && <circle cx={pos.x + 14} cy={pos.y - 14} r={7} fill={theme.warning} />}
                  {blocking > 0 && <text x={pos.x + 14} y={pos.y - 11} textAnchor="middle" fill="#000" fontSize="9" fontWeight="700">{blocking}</text>}
                  <text x={pos.x} y={pos.y + 4} textAnchor="middle" fill={color} fontSize="11" fontWeight="700">{p.id}</text>
                </g>
              );
            })}
          </svg>
        </div>
        <div style={{ display: "flex", gap: "16px", justifyContent: "center", marginTop: "10px", flexWrap: "wrap" }}>
          {[["critical",theme.danger],["high","#FF6B35"],["medium",theme.warning],["low",theme.success]].map(([r,c]) => (
            <div key={r} style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", color: theme.textMuted }}>
              <div style={{ width: "20px", height: "2px", background: c }} />
              {r.charAt(0).toUpperCase() + r.slice(1)} Risk
            </div>
          ))}
          <div style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "10px", color: theme.textMuted }}>
            <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: theme.warning }} />
            = Blocks N projects
          </div>
        </div>
      </div>

      {/* Selected project dependencies */}
      {selected && (
        <div className="pmo-card" style={{ marginBottom: "20px", borderColor: theme.accent }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
            <div className="section-title" style={{ marginBottom: 0 }}>
              {selected} — {getProject(selected)?.name}
            </div>
            <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: theme.textMuted, cursor: "pointer", fontSize: "16px" }}>✕</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
            <div>
              <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "8px", fontWeight: 600 }}>DEPENDS ON (Predecessors)</div>
              {DEPENDENCIES.filter(d => d.to === selected).length === 0
                ? <div style={{ fontSize: "12px", color: theme.textMuted }}>No predecessors</div>
                : DEPENDENCIES.filter(d => d.to === selected).map((dep, i) => (
                  <div key={i} style={{ padding: "8px 12px", background: theme.bg, borderRadius: "6px", marginBottom: "6px", borderLeft: `3px solid ${riskColor(dep.risk)}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: riskColor(dep.risk), fontWeight: 600, fontSize: "12px" }}>{dep.from}</span>
                      <span style={{ fontSize: "10px", color: theme.textMuted }}>{TYPE_LABELS[dep.type]}</span>
                    </div>
                    <div style={{ fontSize: "11px", color: theme.textSecondary, marginTop: "3px" }}>{dep.desc}</div>
                  </div>
                ))
              }
            </div>
            <div>
              <div style={{ fontSize: "11px", color: theme.textMuted, marginBottom: "8px", fontWeight: 600 }}>BLOCKS (Successors)</div>
              {DEPENDENCIES.filter(d => d.from === selected).length === 0
                ? <div style={{ fontSize: "12px", color: theme.textMuted }}>No successors</div>
                : DEPENDENCIES.filter(d => d.from === selected).map((dep, i) => (
                  <div key={i} style={{ padding: "8px 12px", background: theme.bg, borderRadius: "6px", marginBottom: "6px", borderLeft: `3px solid ${riskColor(dep.risk)}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: riskColor(dep.risk), fontWeight: 600, fontSize: "12px" }}>{dep.to}</span>
                      <span style={{ fontSize: "10px", color: theme.textMuted }}>{TYPE_LABELS[dep.type]}</span>
                    </div>
                    <div style={{ fontSize: "11px", color: theme.textSecondary, marginTop: "3px" }}>{dep.desc}</div>
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      )}

      {/* Dependency Register */}
      <div className="pmo-card">
        <div className="section-title">Dependency Register</div>
        <table>
          <thead>
            <tr>{["From","To","Type","Risk Level","Cascade Impact","Description"].map(h => <th key={h}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {DEPENDENCIES.sort((a, b) => {
              const order = { critical: 0, high: 1, medium: 2, low: 3 };
              return order[a.risk] - order[b.risk];
            }).map((d, i) => {
              const fromProj = getProject(d.from);
              const toProj   = getProject(d.to);
              const cascadeRisk = (fromProj?.failProb || 0) > 50 ? "HIGH" : (fromProj?.failProb || 0) > 25 ? "MEDIUM" : "LOW";
              return (
                <tr key={i} style={{ cursor: "pointer" }} onClick={() => setSelected(d.from)}>
                  <td>
                    <span style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.accent, fontSize: "12px" }}>{d.from}</span>
                    <div style={{ fontSize: "10px", color: theme.textMuted, maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {fromProj?.name}
                    </div>
                  </td>
                  <td>
                    <span style={{ fontFamily: "'Share Tech Mono',monospace", color: theme.cyan, fontSize: "12px" }}>{d.to}</span>
                    <div style={{ fontSize: "10px", color: theme.textMuted, maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {toProj?.name}
                    </div>
                  </td>
                  <td>
                    <span style={{ fontSize: "11px", padding: "2px 6px", background: `${theme.accent}15`, color: theme.accent, borderRadius: "4px" }}>
                      {d.type} — {TYPE_LABELS[d.type]}
                    </span>
                  </td>
                  <td>
                    <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: 700, background: `${riskColor(d.risk)}20`, color: riskColor(d.risk) }}>
                      {d.risk.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <span style={{ padding: "2px 8px", borderRadius: "10px", fontSize: "10px", fontWeight: 700, background: cascadeRisk === "HIGH" ? `${theme.danger}20` : cascadeRisk === "MEDIUM" ? `${theme.warning}20` : `${theme.success}20`, color: cascadeRisk === "HIGH" ? theme.danger : cascadeRisk === "MEDIUM" ? theme.warning : theme.success }}>
                      {cascadeRisk}
                    </span>
                  </td>
                  <td style={{ fontSize: "11px", maxWidth: "250px" }}>{d.desc}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </PageLayout>
  );
}
